import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DetachElementsDialogComponent.css';
import { DetachDialogRequest, DetachLevel, useDetachDialogStore } from '../../store/detachDialogStore';
import { DetachRoomElementsBody } from '../../services/coreApi/BundleDetachResult';
import { ChevronDownIcon, CloseIcon, InfoIcon } from '../Icons';
import { SELECTED_COLOR } from './BundleInspectorPanelComponent';

const LEVELS: DetachLevel[] = ['apartment', 'room'];

// The request body for the dialog's inputs. The name is sent only when filled; targetRoomId always, null for apartment level.
export const buildDetachBody = (request: DetachDialogRequest, level: DetachLevel, roomId: string, name: string): DetachRoomElementsBody => {
    const body: DetachRoomElementsBody = {
        roomElementIds: request.elements.flatMap(e => e.roomElementIds),
        targetRoomId: level === 'room' ? Number(roomId) : null,
    };
    if (name.trim()) body.newBundleOverwriteProps = { name: name.trim() };
    return body;
};

// Mounted only while open, so every open starts from the request's defaults
const DetachElementsDialogComponent = () => {
    const request = useDetachDialogStore((s) => s.request);
    return request ? <DetachElementsDialog key={request.bundleName + request.elements.map(e => e.globalId).join()} request={request} /> : null;
};

/**
 * Sets up the bundle the picked elements are detached into: its name, and its level and room.
 * The command sends the request; while it runs nothing can be changed and the dialog can't be closed.
 */
const DetachElementsDialog = ({ request }: { request: DetachDialogRequest }) => {
    const { t } = useTranslation();
    const busy = useDetachDialogStore((s) => s.busy);
    const error = useDetachDialogStore((s) => s.error);
    const [level, setLevel] = useState<DetachLevel>(request.defaultLevel);
    // A default room missing from the list would leave the select showing nothing while counting as chosen
    const [roomId, setRoomId] = useState(request.rooms.some(r => r.id === request.defaultRoomId) ? String(request.defaultRoomId) : '');
    const [name, setName] = useState('');
    const nameRef = useRef<HTMLInputElement>(null);
    const uid = useId();

    useEffect(() => nameRef.current?.focus(), []);

    const key = (k: string) => `command.detachBundleElements.dialog.${k}`;
    const count = request.elements.length;
    const needRoom = level === 'room' && !roomId;
    const canSubmit = !needRoom && !busy && count > 0;
    const rooms = [...request.rooms].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    const submit = () => {
        if (canSubmit) request.onConfirm(buildDetachBody(request, level, roomId, name));
    };
    const cancel = () => {
        if (!busy) request.onCancel();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
        }
    };

    // ←/→ (and ↑/↓) move and select, like the other segmented controls
    const onLevelKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const next = (index + step + LEVELS.length) % LEVELS.length;
        setLevel(LEVELS[next]);
        (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
    };

    return (
        <div className="detach-dialog-overlay" onClick={cancel}>
            <div className="detach-dialog" role="dialog" aria-modal="true" aria-labelledby={`${uid}-title`}
                 onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown}>
                <div className="detach-dialog-header">
                    <span className="detach-dialog-dot" style={{ backgroundColor: SELECTED_COLOR }} aria-hidden="true" />
                    <div className="detach-dialog-heading">
                        <div id={`${uid}-title`} className="detach-dialog-title">{t(key('title'))}</div>
                        <div className="detach-dialog-subtitle">{t(key('from'), { name: request.bundleName })}</div>
                    </div>
                    <button type="button" className="detach-dialog-close" onClick={cancel} disabled={busy} aria-label={t('common.close')}>
                        <CloseIcon />
                    </button>
                </div>

                <div className="detach-dialog-body">
                    <section className="detach-dialog-section">
                        <div className="detach-dialog-section-title">{t(key('elements'), { count })}</div>
                        <ul className="detach-dialog-list">
                            {request.elements.map(e =>
                                <li key={e.globalId} className="detach-dialog-element">
                                    <span className="detach-dialog-element-name">{e.name ?? e.globalId}</span>
                                    {e.roomNames.length > 0 && <span className="detach-dialog-element-room">{e.roomNames.join(', ')}</span>}
                                </li>)}
                        </ul>
                    </section>

                    <section className="detach-dialog-section">
                        <div className="detach-dialog-section-title">{t(key('newBundle'))}</div>
                        <label className="detach-dialog-field">
                            <span className="detach-dialog-label">{t(key('name'))}</span>
                            <input ref={nameRef} value={name} disabled={busy}
                                   placeholder={t(key('namePlaceholder'), { name: request.bundleName })}
                                   onChange={(e) => setName(e.target.value)}
                                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }} />
                        </label>
                        <div className="detach-dialog-field">
                            <span id={`${uid}-level`} className="detach-dialog-label">{t(key('level'))}</span>
                            <div className="detach-dialog-segmented" role="radiogroup" aria-labelledby={`${uid}-level`}>
                                {LEVELS.map((l, i) =>
                                    <button key={l} type="button" role="radio" aria-checked={l === level} tabIndex={l === level ? 0 : -1}
                                            className={`detach-dialog-segment${l === level ? ' selected' : ''}`} disabled={busy}
                                            onClick={() => setLevel(l)} onKeyDown={(e) => onLevelKeyDown(e, i)}>
                                        {t(key(`levels.${l}`))}
                                    </button>)}
                            </div>
                            {level === 'apartment' && <span className="detach-dialog-help">{t(key('apartmentHelp'))}</span>}
                        </div>
                        {level === 'room' &&
                            <label className="detach-dialog-field">
                                <span className="detach-dialog-label">{t(key('room'))}</span>
                                <span className="detach-dialog-select">
                                    <select value={roomId} required disabled={busy} aria-describedby={needRoom ? `${uid}-room-help` : undefined}
                                            onChange={(e) => setRoomId(e.target.value)}>
                                        <option value="" disabled>{t(key('roomPlaceholder'))}</option>
                                        {rooms.map(r => <option key={r.id} value={String(r.id)}>{r.name ?? r.id}</option>)}
                                    </select>
                                    <ChevronDownIcon />
                                </span>
                                {needRoom && <span id={`${uid}-room-help`} className="detach-dialog-help">{t(key('roomHelp'))}</span>}
                            </label>}
                    </section>

                    <p className="detach-dialog-note">
                        <InfoIcon />
                        <span>{t(key('note'), { name: request.bundleName })}</span>
                    </p>

                    {error && <p className="detach-dialog-error" role="alert">{t(key(`errors.${error}`))}</p>}
                </div>

                <div className="detach-dialog-footer">
                    <button type="button" className="detach-dialog-btn-cancel" onClick={cancel} disabled={busy}>
                        {t('common.cancel')}
                    </button>
                    <button type="button" className="detach-dialog-btn-primary" onClick={submit} disabled={!canSubmit}>
                        {busy ? t(key('detaching')) : t(key('detach'), { count })}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetachElementsDialogComponent;
